<template>
  <AutoDialogDrawer
    id="login-policy-dialog"
    placement="centre"
    modal
    dismissible
    @close="$emit('close')"
  >
    <template #title
      >{{ readOnlyView ? translate('View Login Policy') : translate('Edit Login Policy') }}:
      {{ translate('Version') }} {{ loginPolicy.version }}</template
    >
    <p v-if="language === 'nl-NL'">
      We laten <em>Contact</em> zien als we een loginpoging blokeren. Mensen kunnen dit gebruiken om
      vragen te stellen over het inlogbeleid.
      <strong>Dit moet een email van jouw organisatie zijn.</strong> Wie deze mailbox in de gaten
      houdt kan contact opnemen met Ekco indien dat nodig is.
    </p>
    <p v-else>
      <em>Contact</em> is shown when someone's blocked from signing in. People can use this to
      inquire about the login policy.
      <strong>This should be an email address of your company.</strong> Whoever is monitoring the
      inbox can contact Ekco when needed.
    </p>
    <BootstrapFormControl
      v-model="contact"
      class="field"
      name="Contact"
      :disabled="disabled"
      required
      style="margin-bottom: 1rem"
      :error="contactErrors.join('\n')"
      :server-error="!!contactErrors.length"
    />

    <BootstrapCheck
      id="login-policy-ip-restrictions-enabled"
      v-model="ipCheckEnabled"
      allow-wrap
      :label="translate('IP restrictions')"
      :disabled="disabled"
    />

    <!-- overflow hidden is needed, otherwise the dialog window gets a scrollbar
         partway through the animation of the triangle icon rotating -->
    <VueMultiSelectSearch
      id="login-policy-ips"
      :model-value="allowedIpRanges.map((v) => v.toString(true))"
      :name="translate('Allowed IPs')"
      :multiple="true"
      placeholder="CIDR or individual IP"
      :options="[]"
      :taggable="inputContainsIp"
      :disabled="!ipCheckEnabled || disabled"
      :style="{overflow: 'hidden'}"
      :input-group="false"
      :error="allowedIpRangesErrors.join('\n')"
      class="field"
      @remove="
        (toRemove) =>
          (allowedIpRanges = allowedIpRanges.filter((v) => v.toString(true) !== toRemove))
      "
      @search="(value) => (inputContainsIp = isCidr(value))"
      @tag="addCidr"
    />

    <div
      v-if="ipCheckEnabled && (userIpv4MissingFromWhitelist || userIpv6MissingFromWhitelist)"
      style="margin-bottom: 1rem"
    >
      <p class="m-0 text-danger">{{ translate('Warning, your IP is not on the allow list') }}</p>
      <ul>
        <li v-if="userIpv4MissingFromWhitelist">
          {{ ipStore.ip4Label }}
          <BootstrapButton
            name="add-ip4"
            style="display: inline flex"
            variant="primary"
            size="sm"
            class="d-inline-flex"
            :aria-label="translate('Add IPv4 Address')"
            :disabled="disabled"
            @click="ipStore.ip4 && addCidr(ipStore.ip4)"
            >+</BootstrapButton
          >
        </li>
        <li v-if="userIpv6MissingFromWhitelist">
          {{ ipStore.ip6Label }}
          <BootstrapButton
            name="add-ip6"
            variant="primary"
            size="sm"
            class="d-inline-flex"
            :aria-label="translate('Add IPv6 Address')"
            :disabled="disabled"
            @click="ipStore.ip6 && addCidr(ipStore.ip6)"
            >+</BootstrapButton
          >{{ ' ' }}
          <BootstrapButton
            id="login-policy.dialog.add-ip6-subnet"
            style="display: inline flex"
            variant="primary"
            size="sm"
            class="d-inline-flex"
            :aria-label="translate('Add IPv6 Subnet /64')"
            :disabled="disabled"
            @click="ipStore.ip6 && addCidr(ipStore.ip6 + '/64')"
            >+ /64</BootstrapButton
          >
        </li>
      </ul>
    </div>

    <div>
      <p style="margin-bottom: 0">{{ translate('Allowed sign-in methods') }}:</p>
      <BootstrapCheck
        id="login-policy-google"
        v-model="google"
        allow-wrap
        label="Google"
        :disabled="disabled"
      />
      <BootstrapCheck
        id="login-policy-microsoft"
        v-model="microsoft"
        allow-wrap
        label="Microsoft"
        :disabled="disabled"
      />
      <BootstrapCheck
        id="login-policy-email-otp"
        v-model="email_otp"
        allow-wrap
        label="Email OTP"
        :disabled="disabled"
      />
      <div v-if="signInMethodsErrors.length">
        <p v-for="(error, index) in signInMethodsErrors" :key="index" class="text-danger">
          {{ error }}
        </p>
      </div>
    </div>

    <template v-if="otherErrors.length > 0">
      <h5>Errors:</h5>
      <p v-for="(error, index) in otherErrors" :key="index" class="text-danger">{{ error }}</p>
    </template>

    <template #buttons>
      <BootstrapButton
        name="cancel"
        variant="secondary"
        :disabled="isSaving"
        @click="$emit('close')"
      >
        {{ readOnlyView ? translate('Close') : translate('Cancel') }}
      </BootstrapButton>
      <BootstrapButton
        v-if="!readOnlyView"
        name="submit"
        variant="primary"
        :disabled="disabled"
        @click="save"
      >
        {{ translate('Save') }}
      </BootstrapButton>
    </template>
  </AutoDialogDrawer>
</template>

<script setup lang="ts">
import {LoginPolicy} from '@/api/apolloPlatform/loginPolicy';
import {ErrorMap, errorResponseToErrorMap} from '@/sphere/error';
import {IPv4, IPv4CIDR, IPv6, IPv6CIDR, parseAnyCidr, parseAnyIp} from '@/sphere/iputil';

const {translate, language} = useLocalisation();
const loginPolicyStore = useLoginPolicyStore();
const ipStore = useIpStore();
const userStore = useUserStore();

const props = defineProps<{
  loginPolicy: LoginPolicy;
  openReadOnly: boolean;
}>();

const readOnlyView = computed(
  () => props.openReadOnly || !userStore.hasPermission('login-policy:write'),
);

const errors = ref<ErrorMap>({});
function getErrors(type: string): string[] {
  return errors.value[type] ?? [];
}

const contactErrors = computed(() => getErrors('contact'));
const allowedIpRangesErrors = computed(() => getErrors('allowed_ip_ranges'));
const signInMethodsErrors = computed(() => getErrors('sign_in_methods'));

const otherErrors = computed(() => {
  const errorEntries = Object.entries(errors.value).filter(
    ([key, _]) => !['contact', 'allowed_ip_ranges', 'sign_in_methods'].includes(key),
  );
  errorEntries.sort(([key1, _1], [key2, _2]) => key1.localeCompare(key2));
  return errorEntries.flatMap(([_, value]) => value);
});

const contact = ref(props.loginPolicy.contact);
const ipCheckEnabled = ref(props.loginPolicy.ipCheckEnabled);
const allowedIpRanges = ref(props.loginPolicy.allowedIpRanges.map((value) => parseAnyCidr(value)));

const inputContainsIp = ref(false);

const google = ref(props.loginPolicy.google);
const microsoft = ref(props.loginPolicy.microsoft);
const email_otp = ref(props.loginPolicy.email_otp);

function isCidr(value: string) {
  try {
    parseAnyCidr(value);
    return true;
  } catch {
    return false;
  }
}

function addCidr(item: string) {
  if (isCidr(item)) {
    allowedIpRanges.value.push(parseAnyCidr(item));
  }
}

function cidrContainsIp(cidr: IPv4CIDR | IPv6CIDR, ip: IPv4 | IPv6) {
  if (cidr instanceof IPv4CIDR && ip instanceof IPv4) return cidr.contains(ip);
  if (cidr instanceof IPv6CIDR && ip instanceof IPv6) return cidr.contains(ip);
  return false;
}

function ipIncludedInAllowList(ipString: string): boolean {
  const ip = parseAnyIp(ipString);
  return allowedIpRanges.value.some((cidr) => cidrContainsIp(cidr, ip));
}

const userIpv4MissingFromWhitelist = computed(
  () => !!ipStore.ip4 && !ipIncludedInAllowList(ipStore.ip4),
);
const userIpv6MissingFromWhitelist = computed(
  () => !!ipStore.ip6 && !ipIncludedInAllowList(ipStore.ip6),
);

watch(
  ipCheckEnabled,
  (enabled) => {
    if (enabled) ipStore.loadIps();
  },
  {immediate: true},
);

const emit = defineEmits<{
  close: [];
}>();

const isSaving = ref(false);
const disabled = computed(() => readOnlyView.value || isSaving.value);

async function save() {
  errors.value = {};
  isSaving.value = true;
  try {
    await loginPolicyStore.editPolicy(props.loginPolicy.id, {
      contact: contact.value,
      ipCheckEnabled: ipCheckEnabled.value,
      allowedIpRanges: allowedIpRanges.value,
      google: google.value,
      microsoft: microsoft.value,
      email_otp: email_otp.value,
    });
    emit('close');
  } catch (err) {
    errors.value = errorResponseToErrorMap(err);
  } finally {
    isSaving.value = false;
  }
}
</script>
<style lang="scss" scoped>
.field {
  margin-bottom: 1rem;
}

#login-policy-dialog {
  width: auto !important;
  max-width: none;
  margin-left: 20%;
  margin-right: 20%;
}
</style>
